export default {
  app: {
    terminal: "Company Operations",
    description: "Warehouse Management System",
    tagline: "From company registration to declaration and receiving — fully trackable",
  },
  common: {
    loading: "Loading…",
    retry: "Retry",
    save: "Save",
    submit: "Submit",
    cancel: "Cancel",
    close: "Close",
    edit: "Edit",
    delete: "Delete",
    back: "Back",
    next: "Next",
    previous: "Previous",
    search: "Search",
    all: "All",
    pending: "Pending",
    completed: "Completed",
    error: "Action incomplete, please check and retry",
    required: "This field is required",
    noData: "No records yet",
    savedDraft: "Draft saved automatically",
    leaveWarning: "You have unsaved changes. Leave anyway?",
    upload: "Choose file",
    remove: "Remove",
    previousOption: "Previous option",
    nextOption: "Next option",
    qrCode: "QR code",
    skip: "Skip to main content",
    backHome: "Back to home",
  },
  nav: {
    home: "Home",
    declarations: "My Declarations",
    payment: "Payment",
    receiving: "Receiving",
    receivingOrders: "Receiving Orders",
    work: "Work",
    selfOperation: "Self Storage",
    received: "Received",
    profile: "Me",
    logout: "Log out",
    homeMarker: "H",
    declarationsMarker: "D",
    paymentMarker: "P",
    receivingMarker: "R",
    profileMarker: "M",
  },
  selfOperation: {
    home: {
      eyebrow: "SELF OPERATION",
      title: "Self Storage",
      lead: "Self-service stock-in and stock-out inside your rented exclusive areas.",
      overdueBanner: "This contract has overdue unsettled bills; new stock-in is blocked until the lessor confirms settlement. Stock-out is not affected.",
      contractTitle: "My rental contracts",
      validUntil: "Valid until",
      areas: "Contract areas",
      entries: "Quick entries",
      inventory: "Inventory",
      stockIn: "Self stock-in",
      stockOut: "Self stock-out",
      empty: "No operable contracts",
    },
    inventory: {
      title: "My inventory",
      keywordPlaceholder: "Search product name/code",
      empty: "No inventory in contract areas yet",
      available: "Available",
      onHand: "On hand",
      frozen: "Frozen",
      batch: "Batch",
    },
    common: {
      status: "Status",
      version: "Version",
      submit: "Submit",
      cancel: "Cancel order",
      cancelReasonPlaceholder: "Cancel reason (optional)",
      cancelConfirm: "Cancel this order?",
      remark: "Remark",
      empty: "No orders yet",
      loadMore: "Load more",
      quantity: "Quantity",
      location: "Location",
      product: "Product",
      statusPendingPutaway: "Pending putaway",
      statusPartiallyPutaway: "Partially put away",
      statusPutawayCompleted: "Put away",
      statusPendingPick: "Pending pick",
      statusPartiallyPicked: "Partially picked",
      statusPickedCompleted: "Picked",
      statusCancelled: "Cancelled",
      overdueBlockedCopy: "This contract has overdue unsettled bills; new stock-in is blocked until the lessor confirms settlement",
    },
    stockIn: {
      listTitle: "Self stock-in orders",
      createTitle: "New stock-in order",
      detailTitle: "Stock-in order detail",
      addLine: "Add product",
      batchNoPlaceholder: "Batch no. (optional)",
      createSubmit: "Create order",
      createSuccess: "Order created",
      putaway: "Putaway",
      putawayTitle: "Execute putaway",
      addAllocation: "Add putaway allocation",
      allocationQuantity: "Putaway quantity",
      putawaySubmit: "Submit putaway",
      putawaySuccess: "Putaway submitted",
      progress: "Putaway progress",
      declaredQuantity: "Declared",
      putawayQuantity: "Put away",
    },
    stockOut: {
      listTitle: "Self stock-out orders",
      createTitle: "New stock-out order",
      detailTitle: "Stock-out order detail",
      addLine: "Add product",
      availableHint: "available",
      createSubmit: "Create order",
      createSuccess: "Order created",
      pick: "Pick",
      pickTitle: "Execute picking",
      addAllocation: "Add pick allocation",
      pickSubmit: "Submit picking",
      pickSuccess: "Picking submitted",
      progress: "Picking progress",
      requestedQuantity: "Requested",
      pickedQuantity: "Picked",
      pickCandidateEmpty: "No pickable stock for this product",
    },
  },
  work: {
    eyebrow: "WAREHOUSE WORK",
    title: "Warehouse tasks",
    lead: "Review assigned work or claim an open warehouse task.",
    bucketLabel: "Task scope",
    bucket: { mine: "Mine", claimable: "Claimable", completed: "Completed" },
    taskTypeLabel: "Task type",
    warehouseLabel: "Warehouse",
    allWarehouses: "All warehouses",
    searchLabel: "Search warehouse tasks",
    searchPlaceholder: "Task no. / warehouse code / warehouse name",
    assigneeLabel: "Assignee",
    unassigned: "Unassigned",
    completedAt: "Completed",
    claim: "Claim task",
    release: "Release task",
    loadMore: "Load more",
    releaseTitle: "Release task",
    releaseLead: "Task {no} will return to the open pool. Enter a handoff reason.",
    releaseReason: "Release reason",
    releaseReasonPlaceholder: "For example: shift handoff or equipment unavailable",
    confirmRelease: "Confirm release",
    claimed: "Task claimed. Find it under Mine.",
    released: "Task released. Find it under Claimable.",
    conflict: "The task changed. The list has been refreshed; review its latest state.",
    permissionChanged: "Warehouse access changed and this task is no longer available.",
    commandFailed: "The action did not complete. Check the connection and retry.",
    scan: { camera: "Scan code", image: "Choose code image", resolve: "Resolve", failed: "No valid code was found. Choose another image or enter it manually." },
    putaway: {
      title: "Stock-in putaway", continue: "Continue putaway", scanCamera: "Scan code",
      scanImage: "Choose code image", resolve: "Resolve", scanFailed: "No valid code was found. Choose another image or enter it manually.",
      linesTitle: "Products to put away", multipleProducts: "The scan matched multiple product lines. Select the line for this putaway.",
      progress: "Put away {putaway} / received {received} {unit}", remaining: "Remaining {quantity} {unit}",
      selectProduct: "Select {product}", editorTitle: "Add putaway split", selectLineFirst: "Select a product line first.",
      batchMode: "Batch handling", batchInternal: "System internal batch", batchExisting: "Choose existing batch", batchNamed: "Create named batch",
      batchScanLabel: "Batch number or code", existingBatch: "Existing batch", selectBatch: "Select a batch", newBatchNo: "New batch number",
      locationScanLabel: "Bin code or barcode", locationFact: "Bin: {code}", quantityLabel: "Putaway quantity ({unit})",
      addDraft: "Add to putaway list", draftTitle: "Putaway list", draftErrors: "The putaway list needs attention",
      batchFact: "Batch: {batch}", itemQuantity: "{product} putaway quantity", updateItem: "Update {product}", removeItem: "Remove {product}",
      error: {
        line_unavailable: "The product line is no longer available", quantity_invalid: "The quantity format or precision is invalid",
        quantity_exceeds_remaining: "The quantity exceeds the latest server-side remainder",
      },
      eyebrow: "PUTAWAY EXECUTION", warehouseFallback: "Current task warehouse", detailFailed: "The stock-in task could not be loaded.",
      lookupFailed: "That code could not be resolved. Check it and retry.", submitFailed: "The putaway could not be submitted. Try again later.",
      completed: "This stock-in task has been completely put away.", scanProductTitle: "Resolve a product to put away", productScanLabel: "Product code or barcode",
      submit: "Submit putaway", submitted: "Putaway submitted. The latest server progress is now shown.", retrySubmit: "Retry original submission", abandonIntent: "Abandon submission and edit",
      retryFrozen: "The network did not confirm the result. Retry the original submission or abandon it before editing.",
      validationFailed: "The putaway failed validation. Correct the draft and submit again.",
      conflict: "The task version changed. Latest server facts were loaded; review marked rows. Nothing was resubmitted.",
      permissionChanged: "Warehouse authorization changed and this task has been closed.", unavailableNotice: "This warehouse task is no longer available.",
      leaveWarning: "A putaway draft or retryable submission is still pending. Leave anyway?",
    },
    picking: {
      title: "Stock-out picking", continue: "Continue picking", linesTitle: "Products to pick",
      progress: "Picked {picked} / requested {requested} {unit}", remaining: "Remaining {quantity} {unit}",
      selectProduct: "Select {product}", candidatesTitle: "Pickable inventory", noCandidates: "No pickable inventory matched. Check the scan or inventory status.",
      batchFact: "Batch: {batch}", locationFact: "Bin: {code}", available: "Available {quantity} {unit}",
      candidateQuantity: "Pick quantity for {product} at {location}", addDraft: "Add to picking list", loadMore: "Load more inventory candidates",
      draftTitle: "Picking list", draftErrors: "The picking list needs attention", itemQuantity: "{product} picking quantity",
      updateItem: "Update {product}", removeItem: "Remove {product}",
      error: {
        line_unavailable: "The product line can no longer be picked", candidate_unavailable: "The inventory candidate is no longer available",
        quantity_invalid: "The quantity format or precision is invalid", quantity_exceeds_remaining: "The quantity exceeds the latest line remainder",
        quantity_exceeds_available: "The quantity exceeds current candidate availability",
      },
      eyebrow: "PICKING EXECUTION", warehouseFallback: "Current task warehouse", detailFailed: "The stock-out task could not be loaded.",
      scanTitle: "Filter pickable inventory", scanLabel: "Product, batch, or bin code", candidateFailed: "Pickable inventory could not be loaded. Try again later.",
      selectLineFirst: "Select a product line first.", submitFailed: "The picking request could not be submitted. Try again later.",
      completed: "This stock-out task has been completely picked.", submit: "Submit picking", submitted: "Picking submitted. The latest server progress is now shown.",
      retrySubmit: "Retry original submission", abandonIntent: "Abandon submission and edit",
      retryFrozen: "The network did not confirm the result. Retry the original submission or abandon it before editing.",
      validationFailed: "The picking request failed validation. Correct the draft and submit again.",
      conflict: "The task version changed. Latest server facts were loaded; review marked rows. Nothing was resubmitted.",
      permissionChanged: "Warehouse authorization changed and this task has been closed.", unavailableNotice: "This warehouse task is no longer available.",
      leaveWarning: "A picking draft or retryable submission is still pending. Leave anyway?",
    },
    stockTake: {
      title: "Warehouse stocktake", continue: "Continue counting", eyebrow: "BLIND COUNT EXECUTION",
      stepLocation: "01 · Anchor bin", locationTitle: "Scan the current bin", changeLocation: "Change bin",
      locationScanLabel: "Bin code or barcode", locationScanPlaceholder: "Scan or enter a bin code",
      locationProgress: "Recorded {recorded} / {total} in this bin", locationFailed: "That bin is not part of this stocktake task.",
      stepLine: "02 · Resolve product / batch", lineTitle: "Find a count line", damagedLabel: "Damaged label? Show uncounted",
      anchorFirst: "Scan and confirm the current bin first.", lineScanLabel: "Product code, barcode, or batch number",
      lineScanPlaceholder: "Scan an exact product or batch", ambiguous: "{count} lines matched. Choose the correct batch.",
      noLines: "No matching count lines were found in this bin.", linesFailed: "Count lines could not be loaded. Try again later.",
      batchFact: "Batch: {batch}", recorded: "Recorded", unrecorded: "Not recorded",
      stepCount: "03 · Enter physical count", editorTitle: "Enter physical quantity", selectLineFirst: "Scan or select one count line first.",
      locationLabel: "Bin", batchLabel: "Batch", quantityLabel: "Physical quantity ({unit})",
      rerecord: "Recorded as {quantity} {unit}; saving will replace that physical count.", saveLine: "Save this line", saveAgain: "Replace count",
      zeroWarning: "Confirm that the physical quantity is 0? A blank value is never treated as zero.", confirmZero: "Confirm zero",
      quantityError: { blank: "Enter the physical quantity; blank is not zero.", format: "Enter a non-negative number.", precision: "The quantity exceeds this unit's precision." },
      detailFailed: "The stocktake task could not be loaded.", saveFailed: "This count line could not be saved. Try again later.",
      saved: "This physical count was saved.", validationFailed: "The count failed validation. Your input was preserved.",
      conflict: "The task version changed. Server facts were refreshed and checked; nothing was resubmitted.",
      unknownResult: "The network did not confirm the save result. Refresh and check; nothing will be resent automatically.",
      refreshAndCheck: "Refresh and check", reconciled: "Checked: the server already holds the same physical count.",
      unconfirmed: "Refreshed, but the save was not confirmed. Review the input and save again manually.",
      leaveWarning: "The current count or pending verification is not saved. Discard it and continue?",
      permissionChanged: "Warehouse access changed and stocktake entry has been closed.", unavailableNotice: "This stocktake task is no longer available.",
      reviewTitle: "Counting complete — awaiting Owner / manager review", reviewLead: "H5 never shows book quantity or variance. An Owner or warehouse manager reviews it in the admin app.",
      closed: "This stocktake task is completed or cancelled, so entry is closed.",
    },
    type: { stock_in: "Putaway", stock_out: "Picking", stock_take: "Stocktake" },
    status: {
      pending_putaway: "Pending putaway", partially_putaway: "Partially put away", putaway_completed: "Putaway complete",
      pending_pick: "Pending pick", partially_picked: "Partially picked", picked_completed: "Picking complete",
      counting: "Counting", completed: "Completed", unknown: "Status unavailable",
    },
    empty: { mine: "No assigned tasks", claimable: "No claimable tasks", completed: "No completed tasks" },
  },
  auth: {
    loginTitle: "Welcome back",
    loginLead: "Company owners and invited employees can sign in with a username, email, or international phone number.",
    entryNavigation: "Sign-in help",
    identifier: "Username, email, or phone number",
    password: "Password",
    login: "Log in",
    forgot: "Forgot password?",
    inviteEntry: "Have an invite code?",
    applyEntry: "Register a company",
    inspectInvite: "Verify invite",
    inviteTitle: "Accept employee invitation",
    inviteLead: "Complete account setup to join the company with the invited duties.",
    email: "Email",
    code: "8-digit invite code",
    name: "Name",
    phone: "International phone number",
    dialCode: "Country calling code",
    dialCodePlaceholder: "Choose a country calling code…",
    phoneNumber: "Phone number",
    phonePlaceholder: "Enter the phone number without its country code…",
    phoneInvalid: "Enter a valid phone number",
    inviteProfileConfirmed:
      "Your name and phone number have already been confirmed with this invitation.",
    inviteProfilePartiallyConfirmed:
      "Confirmed invitation details do not need to be entered again.",
    passwordHint: "Use 7–128 characters; no specific character mix is required",
    showPassword: "Show {field}",
    hidePassword: "Hide {field}",
    confirmPassword: "Confirm password",
    passwordMismatch: "Passwords do not match",
    agreementPrefix: "I have read and agree to the ",
    privacy: "Privacy Policy",
    and: " and ",
    terms: "Terms of Service",
    agreementError:
      "Please read and agree to the Privacy Policy and Terms of Service first",
    currentAgreement: "CURRENT AGREEMENT",
    readingDone: "Done reading",
    leaveWarning:
      "Registration is not complete. Your entries will be lost if you leave. Leave anyway?",
    register: "Complete registration",
    forgotTitle: "Reset password",
    forgotLead: "If your email is verified, we will send a reset email.",
    sendReset: "Send reset email",
    forgotAccepted: "Request accepted, please check your email.",
    resetTitle: "Set a new password",
    resetLead:
      "Reset links are single-use and time-limited; request a new one after use or expiry.",
    reset: "Update password",
    resetDone: "Password updated, please log in again.",
    resetLinkInvalid: "This link is invalid or has expired",
    confirmEmailTitle: "Confirm login email",
    confirmEmail: "Confirm email",
    confirmEmailDone: "Email confirmed.",
    confirmEmailInvalid:
      "This confirmation link is invalid or has expired. Register again or return to login.",
    registerAgain: "Register again",
    expired: "Session expired, please log in again.",
    sessionUnavailableTitle: "Unable to restore your session",
    sessionUnavailableLead: "The service is temporarily unreachable. Your session has not been cleared; continue when the connection recovers.",
    sessionUnavailableRetry: "The service is still unreachable. Try again shortly.",
    restoreSession: "Restore and continue",
    captchaRequired: "Please complete the security check and retry.",
    invalidCredentials: "Username, email, phone number, or password is incorrect",
    securityCheck: "Security check",
    captchaLead:
      "To protect your account, a quick calculation is required on this device.",
    verifyCaptcha: "Complete security check",
    captchaWorking: "Verifying…",
    captchaPassed: "Security check completed",
    captchaInvalid: "Security check expired, please retry.",
    captchaFailed: "Security check incomplete, please retry.",
    identityLabels: {
      customer: "Customer",
      receivingStaff: "Receiver",
      tenant_admin: "Tenant administrator",
      business_admin: "Business administrator",
      shipping: "Shipping",
      receiving: "Receiving",
    },
  },
  accountApplication: {
    title: "Register a Company Account",
    lead: "Verify the owner's email to create the company immediately; no platform onboarding review is required.",
    stepsLabel: "Company registration steps",
    stepApplication: "1. Company Profile",
    stepReviewActivation: "2. Verify & Create",
    email: "Email",
    emailInvalid: "Enter a valid email address",
    applicantName: "Applicant name",
    organizationName: "Company name",
    customerType: "Customer type",
    customerTypePlaceholder: "Select a customer type",
    optionsLoading: "Loading application options…",
    optionsUnavailable:
      "Application options could not be loaded. Try again later.",
    phone: "Phone number",
    phoneInvalid: "Enter a valid phone number",
    dialCode: "Country code",
    country: "Country / region",
    countryPlaceholder: "Select an available country / region",
    countriesLoading: "Loading available countries…",
    countriesUnavailable:
      "Available countries could not be loaded. Try again later.",
    note: "Application note",
    submit: "Submit application",
    accepted: "Your application was submitted for platform review.",
    nextSteps:
      "If approved, we will send a single-use activation link valid for 72 hours. Use it to verify your email, choose your login password, and accept the current agreements. The account remains unavailable until completion.",
    backToLogin: "Back to login",
    passwordSetupTitle: "Verify Email and Set Password",
    passwordSetupLead:
      "Your application was approved. Opening this single-use credential proves control of the email. Choose a login password and accept the current agreements to activate access.",
    applyAgain: "Submit another application",
  },
  home: {
    title: "Customer home",
    greeting: "Hello, {name}",
    lead: "Today’s declarations and arrival progress at a glance.",
    declarations: "Total declarations",
    pendingReceiving: "Pending receiving",
    startDeclaration: "New goods declaration",
    registerCustomer: "Complete customer profile",
    notices: "Latest notices",
    customerFallback: "Customer",
    quickLinksLabel: "Quick links",
    notice:
      "Before goods arrive, please complete customer registration, goods declaration and payment voucher upload.",
    role: "Customer service",
    businessTitle: "Business",
    businessAside: "By receiving flow",
    flowTitle: "Receiving flow",
    flowAside: "5 STEPS",
    nextEyebrow: "START HERE",
    nextRegisterTitle: "Complete customer profile first",
    nextRegisterDescription:
      "Register the entity and contact so later declarations can link to it.",
    nextRegisterTag: "Step 1",
    nextDeclarationTitle: "Submit your first goods declaration",
    nextDeclarationDescription:
      "Fill in goods and logistics info to generate a declaration number.",
    nextDeclarationTag: "Step 2",
    nextReceivingTitle: "View pending receiving tasks",
    nextReceivingDescription:
      "Goods are awaiting verification — open the workbench to proceed.",
    nextReceivingTag: "Pending",
    nextTrackTitle: "Track declaration and payment status",
    nextTrackDescription:
      "Check declaration progress and upload payment vouchers when needed.",
    nextTrackTag: "In progress",
    featureCustomer: "Customer Registration",
    featureCustomerEn: "Customer Reg.",
    featureDeclaration: "Goods Declaration",
    featureDeclarationEn: "Goods Decl.",
    featurePayment: "Payment Registration",
    featurePaymentEn: "Payment Reg.",
    featureOrders: "My Declarations",
    featureOrdersEn: "My Decl.",
    featureReceiving: "Receiving Workbench",
    featureReceivingEn: "Receiving",
    featureContact: "Contact Us",
    featureContactEn: "Contact Us",
    commonBusiness: "Common Business",
    declarationDescription: "Create a declaration",
    customerDescription: "Maintain customer profile",
    paymentDescription: "Upload payment voucher",
    primaryReceivingTitle: "Scan to Receive",
    primaryReceivingDescription: "Scan or review assigned tasks",
    staffOnly: "Staff entrance",
    flowStep1: "Register the customer profile to generate a customer number",
    flowStep2:
      "Fill in a goods declaration to generate a declaration number and QR code",
    flowStep3: "Pay and upload the payment voucher for finance review",
    flowStep4: "Goods arrive — staff scan the code to verify and receive",
    flowStep5:
      "Warehousing / sourcing registration completed — fully trackable",
    receivingAddressLabel: "Receiving address",
    receivingHoursLabel: "Business hours",
    noticeTag: "Notice",
    receivingInfoTitle: "Receiving information",
  },
  customer: {
    title: "Customer Registration",
    registerEyebrow: "Customer Profile",
    formSectionsLabel: "Customer profile sections",
    basicTab: "Basic Info",
    additionalTab: "Additional",
    basicTitle: "Customer & Contact",
    basicLead: "Add customer identity, business contact, and primary location details.",
    additionalTitle: "Documents & Notes",
    additionalLead: "Optional details can be completed later and are saved with the customer profile.",
    type: "Customer type",
    name: "Company or individual name",
    contact: "Contact name",
    contactPhone: "Business phone",
    dialCode: "Country code",
    phoneNumber: "Phone number",
    phoneInvalid: "Enter a valid phone number",
    wechat: "WeChat ID",
    country: "Country",
    countryPlaceholder: "Select a country…",
    city: "City",
    cityPlaceholder: "Select a city…",
    selectCountryFirst: "Select a country first…",
    whatsapp: "WhatsApp",
    license: "Business license no.",
    remark: "Remark",
    certificates: "Qualification documents",
    certificatesHint:
      "Images, PDF, and Word; up to 9 files. Long press to reorder",
    addCertificate: "Add qualification document",
    certificateSortHint: "Long press and drag or use arrow keys to reorder",
    certificatePosition: "Current position",
    saved: "Customer profile saved",
    update: "Submit for Review",
    pendingUpdateTitle: "Profile Update Under Review",
    pendingUpdateBody:
      "Your customer profile update is pending platform review. It will take effect automatically after approval. You cannot submit further changes during the review period.",
    pendingUpdateMessage: "Profile update submitted and pending review",
    pendingUpdateBlocked:
      "A profile update is already under review. Please wait for the result before submitting again.",
  },
  declaration: {
    listTitle: "Goods Declarations",
    createTitle: "Goods declaration",
    recordsEntry: "Records",
    shippingDraftNo: "Shipping order number",
    creatingServerDraft: "Creating server draft…",
    serverDraftReady: "Draft created",
    retryCreateDraft: "Retry draft creation",
    addedItemsHeader: "Added items {count}",
    stepDestination: "Destination",
    stepCustomer: "Confirm company",
    stepGoods: "Goods",
    stepLogistics: "Logistics & packages",
    stepWeighingReview: "Weighing ticket",
    stepTransport: "Logistics",
    stepConfirm: "Confirm",
    stepLogisticsReview: "Logistics & review",
    logisticsPackageTitle: "Courier company and package measurements",
    logisticsPackageLead: "Choose the courier company for this shipment, fill in transport details, then record dimensions, weight, and quantity for each package group.",
    logisticsCompanyLead: "Pick the courier company carrying this shipment; company contact details are filled in automatically.",
    logisticsCompanyEmptyBody:
      "Ask an administrator to add and enable at least one courier company under “Base data → Business options → Logistics company” before continuing.",
    logisticsCompanyContact: "Company contact: {contact}",
    contextAutoNote: "Shipper and receiver details are carried over from the declaration automatically.",
    transportDetails: "Transport details",
    packageGroupsTitle: "Package groups",
    packageGroupsLead: "Package groups are independent of goods; one package may contain multiple items.",
    addPackageGroup: "Add package group",
    packageGroup: "Package group {index}",
    lengthCm: "Length (cm)",
    widthCm: "Width (cm)",
    heightCm: "Height (cm)",
    weightG: "Weight per piece (g)",
    previewWeighingTicket: "Preview weighing ticket",
    weighingReviewTitle: "Review the electronic weighing ticket",
    weighingReviewLead: "Freight is calculated by the server rate card; declared goods value is for customs only.",
    goodsValueSeparateNote: "Declared value and freight settlement are recorded separately and are never added without FX conversion.",
    quotePendingTitle: "Rate pending for this route",
    quoteUnavailable: "A freight quote is not currently available.",
    quotePendingSubmitNote: "You can still submit and retry from the declaration detail after a rate is activated.",
    freightSettlement: "Freight settlement",
    payFreightOnline: "Pay freight",
    continueFreightPayment: "Continue payment",
    retryQuote: "Retry quote",
    refundProgress: "Refund {no} · {status}",
    settlementStatus: {
      unquoted: "Awaiting quote",
      estimated_due: "Prepayment due",
      payment_pending: "Confirming payment",
      prepaid: "Prepaid",
      reconciling: "Receiving reconciliation",
      adjustment_due: "Adjustment due",
      refund_pending: "Refund pending",
      settled: "Settled",
      exception: "Settlement exception",
    },
    destination: "Destination",
    destinationTitle: "Where should these goods go?",
    destinationLead:
      "Choose the receiving centre for this declaration. Goods and logistics will be filed under this destination.",
    selected: "Selected",
    destinationEmptyTitle: "No destination is available",
    destinationEmptyBody:
      "Ask an administrator to add and enable a destination under Base Data → Business Options → Declaration Destination before creating a declaration.",
    customerConfirmTitle: "Confirm receiving customer",
    customerConfirmLead:
      "This declaration uses your registered customer profile. Confirm the contact and phone before continuing.",
    confirmed: "Confirmed",
    contactIncomplete: "Contact details incomplete",
    completeContact: "Complete company contact details",
    coordinatePairRequired: "Enter both latitude and longitude",
    coordinateInvalid: "Enter valid latitude and longitude values",
    customerMissingTitle: "Customer profile not registered",
    customerMissingBody:
      "Register your customer profile before continuing with the declaration.",
    registerCustomer: "Register customer profile",
    imagesRequired: "Upload at least one goods image",
    imagesTooMany: "Maximum 3 goods images allowed",
    vouchersTooMany: "Maximum 9 declaration documents allowed",
    imageHint:
      "Up to 9 images; you can photograph the product, packaging, labels and packing condition.",
    addAtLeastOneItem: "Save at least one item first",
    itemSaved: "Item {count} saved. You can add the next item.",
    editCurrentItem: "Edit current item",
    addCurrentItem: "Add goods one at a time",
    itemEditorLead:
      "This page edits one item only. The form clears after saving; use the separate manager to review saved items.",
    existingImages: "{count} existing image(s) retained",
    historyPickerConfirmSingle: "Use this item",
    logisticsAndReviewTitle: "Logistics and final review",
    logisticsAndReviewLead:
      "Add transport details, review the totals, then submit the declaration.",
    savedGoodsCount: "{count} item(s) saved",
    savedItemsShort: "{count} saved",
    manageItems: "Manage items",
    saveItemChanges: "Save changes",
    addAnotherItem: "Add item",
    saveDeclaration: "Save declaration",
    manageItemsTitle: "Draft item manager",
    manageItemsLead:
      "Manage every item in this declaration on a separate page. Changes update the draft only.",
    newItem: "New item",
    saveItem: "Save item",
    itemActions: "Item actions",
    itemQuantitySummary: "{quantity} {unit} · {boxes} boxes",
    itemPackagingSummary:
      "{packaging} · {packages} packages · {quantity} {unit}",
    emptyDraftItems: "This draft has no items yet",
    continueDeclaration: "Continue",
    deleteDraft: "Delete draft",
    deleteDraftTitle: "Delete this draft?",
    deleteDraftBody: "{no} has not become a formal record. Deletion cannot be undone; the deletion action itself remains in the audit log.",
    confirmDeleteDraft: "Delete draft now",
    deleteItemTitle: "Delete item?",
    deleteItemBody:
      "“{name}” will be removed from this draft. An empty draft can be saved but cannot be submitted.",
    recordsTitle: "Declaration records",
    recordsCount: "{count} declaration(s)",
    newDeclaration: "New",
    recordFilters: "Declaration record filters",
    filterDraft: "Drafts",
    draftRecord: "Draft declaration",
    declarationNo: "Declaration number",
    destinationNotRecorded: "Destination not recorded",
    moreItems: "{count} more item(s)",
    recordTotals: "{quantity} items · {boxes} packages",
    recordMetricKinds: "Item types",
    recordMetricPackages: "Packages",
    recordMetricAmount: "Declared amount",
    recordKindsValue: "{count} types",
    recordPackagesValue: "{count} packages",
    viewDetail: "View details",
    pullToRefresh: "Pull to refresh",
    releaseToRefresh: "Release to refresh",
    refreshing: "Refreshing",
    refreshed: "Refreshed",
    noRecordsInFilter: "No declarations match this filter",
    identityHint: "Declaration entity",
    goodsHint: "Goods details",
    confirmLead: "Please review the entity, goods and logistics, then submit.",
    goodsSummary: "Goods summary",
    logisticsSummary: "Logistics summary",
    totalItems: "Total quantity",
    totalBoxes: "Total boxes",
    totalPackages: "Total packages",
    unset: "Not filled",
    purpose: "Goods purpose",
    customerName: "Customer name",
    customerNamePlaceholder:
      "Company or personal name matching your registration",
    contactName: "Contact",
    phone: "Phone",
    phonePlaceholder: "Enter phone number with country code",
    goodsName: "Goods name",
    goodsNamePlaceholder: "e.g. LED desk lamp",
    category: "Category",
    specification: "Specification",
    quantity: "Quantity",
    quantityRuleInvalid: "Check that quantity and packaging calculation agree",
    quantityPrecisionInvalid:
      "Invalid quantity. This unit allows up to {precision} decimal places; 0 means whole numbers only.",
    currencyUnset: "Currency not selected",
    unit: "Unit",
    boxes: "Boxes",
    unitPrice: "Unit price",
    unitPriceWithCurrency: "Unit price ({currency})",
    unitPricePrecisionInvalid:
      "Enter a non-negative amount with no more than {precision} decimal places.",
    quantityAndPackaging: "Quantity and packaging",
    packagingType: "Packaging type",
    quantityMode: "Quantity entry mode",
    quantityModeCalculated: "Calculate from packages",
    quantityModeManual: "Enter quantity directly",
    packageCount: "Package count",
    quantityPerPackageWithUnit: "Quantity per package ({unit})",
    looseQuantityWithUnit: "Loose quantity ({unit})",
    manualQuantityWithUnit: "Total goods quantity ({unit})",
    packagingNote: "Packaging description",
    packagingNotePlaceholder: "Describe the actual packaging",
    calculationReceipt: "Automatic calculation",
    quantityFormulaLabel: "Quantity formula",
    packagingFormula:
      "{packages} × {perPackage} {unit} + {loose} {unit} = {quantity} {unit}",
    manualQuantityFormula: "Direct entry {quantity} {unit} = {quantity} {unit}",
    totalQuantity: "Total quantity",
    itemTotalPrice: "Item total",
    unitFallback: "unit",
    packagingCarton: "Carton",
    packagingWoodenCase: "Wooden case",
    packagingPlasticCrate: "Plastic crate",
    packagingBag: "Bag",
    packagingBale: "Bale / bundle",
    packagingPallet: "Pallet",
    packagingDrum: "Drum",
    packagingLoose: "Loose / unpackaged",
    packagingOther: "Other",
    images: "Goods images (optional)",
    addGoodsImage: "Add goods image",
    goodsImageLimitHint: "Add up to 3 photos of the item, label, or model.",
    goodsRemark: "Goods note",
    goodsRemarkPlaceholder:
      "Optional: fragile handling, colour, or receiving notes…",
    goodsImageHint:
      "Use these only to identify the goods: photograph the item, label, or model. You can continue without one.",
    declarationVouchers: "Declaration documents (optional)",
    addVoucherFromAlbum: "Upload from album",
    takeVoucherPhoto: "Take a photo",
    voucherSortHint: "Long press and drag or use arrow keys to reorder",
    voucherPosition: "Current position",
    declarationVoucherHint:
      "Add invoices, purchase orders, or price screenshots as supporting evidence. These are stored separately from goods images.",
    existingVouchers: "{count} declaration documents retained",
    itemSectionsLabel: "Goods information sections",
    itemTabCategory: "Category",
    itemTabInfo: "Goods info",
    itemTabPackaging: "Quantity & packaging",
    itemTabVouchers: "Documents",
    itemBasicLead:
      "Identify the goods first. A goods image is optional and should show the item itself.",
    itemPackagingLead:
      "Enter the packaging and quantity. The total quantity and value update automatically.",
    itemVouchersLead:
      "Supporting documents explain the price or purchase source and can be added later.",
    categoryQuickLead:
      "Choose the closest category to continue to goods information.",
    categoryEmpty: "No goods categories are available",
    chooseCategoryFirst: "Choose a category in Goods category first.",
    imageCount: "{count} file(s)",
    addItem: "Add item",
    removeItem: "Remove this item",
    removeItemNamed: "Remove goods item {index}",
    pickFromHistory: "Pick from history",
    historyPickerTitle: "Pick from historical goods",
    historyPickerSearch: "Search by name / brand / barcode",
    historyPickerConfirm: "Add to goods details",
    historyPickerEmpty: "No historical goods",
    uncategorized: "Uncategorized",
    expectedArrival: "Expected arrival date",
    shippingOrigin: "Shipping origin",
    logisticsCompany: "Logistics company",
    trackingNo: "Tracking no.",
    trackingNoPlaceholder: "e.g. SF1234567890",
    vehicleDriver: "Plate & driver",
    manager: "Receiving manager",
    managerAssignedHint:
      "The receiving manager is assigned by operations — no customer selection needed",
    transportRemark: "Transport remark",
    submit: "Submit declaration",
    submitted: "Declaration submitted",
    detailTitle: "Declaration detail",
    declarationInfoTitle: "Declaration information",
    attachmentOpenFailed: "Failed to open the attachment",
    supplement: "Supplement info",
    continueEdit: "Continue editing",
    qrFailed: "QR code unavailable — show the declaration number instead",
    copyNo: "Copy number",
    copied: "Declaration number copied",
    copyFallback: "Number selected — copy it manually",
    pay: "Register payment",
    stepsLabel: "Declaration steps",
    filterPendingPayment: "Pending payment",
    filterPendingReceiving: "Pending receiving",
    filterArrived: "Arrived",
    filterAbnormal: "Abnormal",
    goodsSeparator: ", ",
    metaBoxes: "📦 {count} packages",
    metaExpected: "📅 Expected {date}",
    metaSubmitted: "Submitted {date}",
    costsTitle: "Costs and settlement",
    goodsAmount: "Goods amount",
    serviceFee: "Service fee",
    freight: "Freight",
    totalPayable: "Total payable",
    approvedReceived: "Confirmed received",
    refunded: "Refunded",
    logisticsTitle: "Logistics and arrival",
    routeLabel: "Shipping route",
    receivingRecords: "Receiving records",
    boxesAndItems: "{boxes} packages / {quantity} items",
    pendingFollowUp: "Pending follow-up",
    noReceivingRecords: "No receiving records yet",
    timelineTitle: "Business timeline",
    noTimelineEvents: "No business updates yet",
    qrCodeLabel: "Declaration QR code",
    statusPanelLabel: "Business status",
    statusDeclaration: "Declaration",
    statusPayment: "Payment",
    statusReceiving: "Receiving",
    shipmentTitle: "Shipment tracking",
    waybillNo: "Waybill No.",
    carrier: "Carrier",
    currentStatus: "Current status",
    noTrackEvents: "No tracking events yet",
    trackStatus: {
      pending: "Awaiting pickup",
      picked_up: "Picked up",
      in_transit: "In transit",
      customs_hold: "Customs hold",
      delivered: "Delivered",
      exception: "Exception",
      departed: "Departed",
      arrived: "Arrived",
      out_for_delivery: "Out for delivery",
    },
    wizardAssigned: "Assigned by the administrator after submission",
    itemsTitle: "Item details",
    itemsSummary: "{kinds} kinds · {packages} packages · {quantity} total",
    domainsLabel: "Declaration sections",
    tabGoods: "Goods",
    tabLogistics: "Logistics",
    tabReceiving: "Receiving",
    tabRecords: "Records",
    archivedBadge: "Archived",
    noImage: "No product image",
    voucherCount: "Vouchers ({count})",
    voucherTitle: "Declaration vouchers",
    packagesUnit: "packages",
    spec: "Spec",
    noItems: "No item details yet",
    draftSaved:
      "Draft saved to the server — you can continue editing on any device.",
    editTitle: "Supplement or edit declaration",
    saveServerDraft: "Save server draft",
  },
  payment: {
    freightWeighingTicket: "Transparent weighing ticket",
    actualWeight: "Actual weight",
    volumetricWeight: "Volumetric weight",
    chargeableWeight: "Chargeable weight",
    freightPayable: "Freight payable",
    freightFormulaNote: "Each piece uses the greater of actual and volumetric weight, rounded by the rate card before aggregation",
    onlineCheckoutTitle: "Pay freight online",
    onlineCheckoutLead: "Review the weighing ticket and choose an available channel. Only the asynchronous server confirmation is authoritative.",
    onlineStatusTitle: "Online payment status",
    chooseOnlineChannel: "Choose payment channel",
    mpesaPhone: "M-PESA phone",
    mpesaPhoneHint: "An STK Push will be sent to this number for confirmation.",
    noOnlineChannel: "No online channel for this currency",
    offlineAuthorizationOnly: "Offline fallback is available only when an administrator explicitly authorizes it for this declaration.",
    requestMpesa: "Send M-PESA authorization",
    openWechatPay: "Continue to WeChat Pay",
    onlineCreateFailed: "The online payment order could not be created. Try again later.",
    onlinePaidTitle: "Freight payment confirmed",
    onlinePaidLead: "The provider callback confirmed receipt. Return to the declaration for the next step.",
    onlinePendingTitle: "Waiting for payment confirmation",
    mpesaPendingLead: "Approve the M-PESA request on your phone. This page refreshes automatically.",
    wechatPendingLead: "If you completed WeChat Pay, keep this page open while the server confirms it.",
    onlineFailedTitle: "Payment not completed",
    onlineFailedLead: "No confirmed charge was recorded. Return to checkout and try again.",
    retryOnlinePayment: "Retry payment",
    viewDeclaration: "View declaration",
    pollingProvider: "Waiting for provider confirmation…",
    title: "Payment Registration",
    lead: "Upload payment proof and link it to a declaration; the result is confirmed by the backend.",
    listTitle: "My Payments",
    detailTitle: "Payment Detail",
    declaration: "Linked declaration",
    loadingDeclaration: "Loading declaration…",
    chooseDeclaration: "Select a declaration",
    payer: "Payer name",
    amount: "Amount",
    currency: "Currency",
    method: "Payment method",
    date: "Payment date",
    transaction: "Bank or M-Pesa reference no.",
    transactionNo: "Reference no.",
    account: "Payment account",
    actualReceived: "Actual received",
    receivedAccount: "Receiving account",
    reviewResult: "Review result",
    reviewedAt: "Reviewed at",
    financeRemark: "Finance remark",
    vouchers: "Payment vouchers",
    vouchersRequired: "Upload at least one payment voucher.",
    note: "Note",
    submit: "Submit payment registration",
    submitted: "Payment registration submitted",
    refundsTitle: "Refunds",
    noRefunds: "No refunds",
    bankTransfer: "Bank transfer",
    rulesTitle: "Collection & review notes",
    bankAccount: "Bank account",
    serviceChargeRule: "Service charge rule",
    paymentRules: "Review rules",
    payerAccount: "Payer account or M-Pesa number",
    cash: "Cash",
    recordsEntry: "Payment records",
    stepsLabel: "Payment registration steps",
    stepDeclaration: "Select declaration",
    stepDetails: "Payment details",
    chooseTitle: "Choose the declaration to pay",
    chooseLead:
      "Only declarations that can still accept payment are shown. The remaining balance is calculated automatically.",
    noPayableDeclarations:
      "There are no payable declarations. Complete a declaration or check its current status first.",
    viewDeclarations: "View declaration records",
    totalPayable: "Total payable",
    activeRegistered: "Reviewing / received",
    remainingPayable: "Remaining",
    balanceTicket: "Declaration payment balance",
    linkedDeclaration: "Linked declaration",
    detailsTitle: "Enter payment details",
    detailsLead:
      "Enter the actual transaction details and upload clear, complete payment proof.",
    changeDeclaration: "Change declaration",
    changeDeclarationWarning:
      "Changing the declaration will clear the current payment details. Continue?",
    payerAndAmount: "Payer and amount",
    contactPhone: "Contact phone",
    currencyLocked: "Currency is fixed by the declaration",
    chooseMethod: "Select a payment method",
    transactionEvidence: "Transaction evidence",
    bankTransactionNo: "Bank reference no.",
    bankPayerAccount: "Paying account",
    mpesaTransactionNo: "M-Pesa transaction no.",
    mpesaNumber: "M-Pesa number",
    digitalTransactionNo: "Payment transaction no.",
    digitalPayerAccount: "Paying account",
    posTransactionNo: "POS reference no.",
    cardLastFour: "Last four card digits",
    cashReceiptNo: "Receipt no.",
    genericTransactionNo: "Payment evidence no.",
    accountDescription: "Account description (optional)",
    voucherLead:
      "Images and PDFs are supported. You can add or remove files before submitting.",
    addVoucher: "Add payment proof",
    amountPositive: "The payment amount must be greater than zero.",
    amountWholeUnit: "Enter a whole-unit payment amount.",
    amountExceedsRemaining:
      "The payment amount cannot exceed the remaining balance.",
    cardLastFourInvalid: "Enter the last four card digits.",
    reviewAndSubmit: "Review and submit",
    confirmTitle: "Review payment registration",
    confirmLead:
      "Confirm the details match the proof. The registration will enter review after submission.",
    voucherCount: "{count} voucher | {count} vouchers",
    backToEdit: "Back to edit",
    confirmSubmit: "Confirm submission",
    recordsTitle: "Payment records",
    recordsCount:
      "{count} payment registration | {count} payment registrations",
    newPayment: "New payment",
    recordFilters: "Payment record filters",
    filterPendingReview: "Pending review",
    filterPaid: "Paid",
    filterUnsuccessful: "Unsuccessful",
    searchPlaceholder: "Search payment, declaration, reference, or payer",
    pullToRefresh: "Pull to refresh",
    releaseToRefresh: "Release to refresh",
    refreshing: "Refreshing",
    refreshed: "Refreshed",
    noRecordsInFilter: "No payment records match this filter",
    viewDetail: "View details",
    paymentNo: "Payment no.",
    createdAt: "Registered at",
    paymentInfoTitle: "Payment information",
    reviewInfoTitle: "Review result",
    voucherFilesTitle: "Payment proof",
    voucherUploaded: "Uploaded",
    openVoucher: "Open proof",
    noVouchers: "No payment proof",
    attachmentOpenFailed:
      "The payment proof could not be opened. Try again later.",
    continuePayment: "Continue payment",
    retryPayment: "Register again",
  },
  message: {
    inboxTitle: "Messages",
    detailTitle: "Message Detail",
    markAllRead: "Mark all read",
    markAllDone: "All messages marked as read",
    unread: "Unread",
    empty: "No messages yet",
    emptyBody: "This message has no body content.",
    openAction: "View details",
    featureEntry: "Messages",
    featureEntryEn: "INBOX",
  },
  receiving: {
    title: "Receiving workbench",
    heroAccount: "Receiving workbench · {name}",
    lead: "Find receiving tasks assigned to me by scan, image or manual entry.",
    manager: "Receiving manager",
    query: "Declaration no., tracking no., customer name, or phone",
    scan: "Scan QR code",
    image: "Recognize QR image",
    manual: "Manual entry",
    find: "Find task",
    verifyTitle: "Receiving verification",
    compareTitle: "Customer declaration data (verify item by item)",
    compareCustomer: "Customer: {name} ｜ Phone: {phone}",
    compareItem: "{name}: {quantity} {unit} / {boxes} packages",
    compareTotal: "Total packages: {boxes} ｜ Logistics: {logistics}",
    itemizedTitle: "Itemized actuals",
    itemizedLead:
      "Enter the goods actually received this time. The server will reject the submission if the cumulative total exceeds the declared quantity.",
    declaredLine: "Declared: {boxes} packages / {quantity} {unit}",
    boxesVsQuantity: "{boxes} packages / {quantity} {unit}",
    boxesOnly: "{boxes} packages",
    actualBoxes: "Actual packages",
    actualQuantity: "Actual quantity",
    actualWeight: "Actual weight (g)",
    actualVolume: "Actual volume (cm³)",
    condition: "Package & declaration check result",
    warehouse: "Warehouse location",
    abnormalType: "Abnormal type",
    abnormalDescription: "Abnormal description",
    photos: "Receiving photos",
    receiverName: "Receiver name",
    submit: "Confirm receiving result",
    submitted: "Receiving result submitted",
    scanDeclaration: "Identify declaration QR code",
    scanLead: "Scan the code in front of you or choose an existing image.",
    searchPlaceholder: "Declaration / tracking no. / customer / phone…",
    searchSubmit: "Search",
    heroStats: "{pending} assigned · {completed} closed",
    scanPrimaryTitle: "Scan to Receive",
    scanPrimaryLead: "Identify a declaration QR code and open its verification task",
    pendingLabel: "Pending",
    completedLabel: "Completed",
    scanMethodTitle: "Choose a Scan Method",
    scannerTicketTitle: "QR Receiving Desk",
    scanActionLead: "Scan the on-site QR code to open verification",
    imageActionLead: "Choose an existing QR image from your gallery",
    scanAutoOpenHint: "A recognized declaration QR opens verification automatically",
    lookupNoExactMatch: "No receiving order exactly matches this QR code. Use the search above instead.",
    lookupFailed: "The receiving order for this QR code could not be loaded. Try again.",
    ordersTitle: "Receiving Orders",
    ordersLead: "Find and process receiving work assigned to me",
    statsUnavailable: "Counts are temporarily unavailable",
    tasksTitle: "Pending Receiving Tasks",
    tasksLead: "Only tasks explicitly assigned to me are shown",
    completedEntry: "Receiving Records",
    managerLine: "Manager: {name}",
    packagesLine: "{count} packages",
    managerUnset: "Unassigned",
    empty: "No receiving tasks assigned to you",
    completedTitle: "Received Goods",
    completedDetailTitle: "Received Goods Details",
    completedEmpty: "No received goods yet",
    completedAt: "Completed at",
    readOnlyNotice:
      "This receiving task is closed. The records below are read-only.",
    receivedBoxes: "Received {count} packages",
    recordNumber: "Receiving number",
    receivedSummary: "{boxes} packages / {quantity} items",
    itemReceivedLine: "{boxes} packages / {quantity} {unit}",
    weightValue: "{value} g",
    volumeValue: "{value} cm³",
    matched: "Matches declaration",
    damaged: "Package damaged",
    shortage: "Quantity shortage",
    none: "No abnormality",
    other: "Other abnormality",
  },
  profile: {
    title: "My account",
    account: "Account info",
    verified: "Verified",
    unverified: "Unverified",
    pendingEmail: "Pending email confirmation",
    changeEmail: "Change login email",
    newEmail: "New login email",
    currentPassword: "Current password",
    sendConfirmation: "Send confirmation email",
    sent: "Confirmation email sent.",
    language: "Language",
    identity: "Identity",
    identityDual: "Customer + Receiver",
    identityStaffInvite: "Receiver (invite-only)",
    inviteOnly: "invite-only",
    accountSettings: "Account Settings",
    preferences: "Preferences",
    unnamed: "No name set",
    avatar: "Avatar",
    changeAvatar: "Change avatar",
    editAvatar: "Edit Avatar",
    avatarUpdated: "Avatar updated",
    avatarInvalidType: "Please select an image file",
    avatarTooLarge: "Image size cannot exceed 2MB",
    avatarPreview: "Avatar preview",
    avatarHint: "Tap the circle above to select an image",
    avatarSelect: "Select Image",
    avatarReselect: "Reselect",
    avatarConfirm: "Confirm",
    avatarRemove: "Remove Avatar",
    avatarDiscard: "Discard Changes",
    avatarRemoveConfirmTitle: "Remove avatar?",
    avatarRemoveConfirmBody:
      "Your default avatar will be restored. Do you want to continue?",
    avatarRemoveConfirm: "Remove",
  },
  legal: {
    privacyTitle: "Privacy Policy",
    privacyBody:
      "We only process information necessary for account verification, customer registration, goods declaration, payment registration and arrival verification. Login credentials, one-time links and business attachments are stored and accessed under least-privilege principles.",
    termsTitle: "Terms of Service",
    termsBody:
      "Using this terminal means you will provide truthful and complete business information and safeguard your account. Key submissions are subject to backend confirmation; contact operations staff promptly if anything seems wrong.",
    version: "Version",
    currentEffective: "Currently in effect",
    notConfigured:
      "The agreement is not configured yet — please contact the administrator before applying.",
    loadingRetry: "Loading the current agreement — please retry shortly.",
  },
  errors: {
    firstInvalid: "Please fix the highlighted fields before continuing.",
    inviteInvalid: "Invite is invalid, used, revoked or expired.",
    scanFailed:
      "No valid code recognized — try image or manual lookup instead.",
    unknownStatus: "Unknown status",
    notFound: "Page not found",
  },
};
